// src/lib/firebase.ts
import { initializeApp } from "firebase/app";
import { 
  getFirestore, 
  connectFirestoreEmulator, 
  Firestore, 
} from "firebase/firestore";
import { getAuth, connectAuthEmulator, Auth } from "firebase/auth";
import { getStorage, FirebaseStorage } from "firebase/storage";
import { getFunctions, connectFunctionsEmulator, Functions, httpsCallable } from "firebase/functions";
// Enable debug logging for Firestore
//import { setLogLevel } from "firebase/firestore";
//setLogLevel("debug");

// Enable debug logging for Firestore in development
if (import.meta.env.DEV) {
  console.log("Enabling Firestore debug logging mode");
  // setLogLevel("debug"); // Commented out to disable verbose logs
}

console.log("Initializing Firebase");

// Use environment variables for Firebase configuration
const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Basic check to ensure variables are loaded
if (!firebaseConfig.apiKey) {
    console.error("Firebase config variables not found. Make sure you have a .env file with VITE_FIREBASE_* variables.");
    // Optionally, you could throw an error here or provide default fallbacks if suitable
}

// Add Retry Logic for Firebase initialization
let app;
let retryCount = 0;
const MAX_RETRIES = 3;

function initializeFirebase() {
    try {
        app = initializeApp(firebaseConfig);
        console.log("Firebase app initialized");
        return app;
    } catch (error) {
        console.error(`Firebase initialization error (attempt ${retryCount + 1}/${MAX_RETRIES}):`, error);
        
        if (retryCount < MAX_RETRIES - 1) {
            retryCount++;
            console.log(`Retrying Firebase initialization in 1 second...`);
            setTimeout(initializeFirebase, 1000);
        } else {
            console.error("Max retries reached. Firebase initialization failed.");
            throw error; // Re-throw after max retries
        }
    }
}

// Initialize Firebase with retry
app = initializeFirebase();

// Initialize Firebase services with error handling
let db: Firestore, auth: Auth, storage: FirebaseStorage, functions: Functions;

// Make sure app is defined before proceeding
if (!app) {
    console.error("Firebase app is not initialized. Creating empty services to prevent crashes.");
    db = {} as Firestore;
    auth = {} as Auth;
    storage = {} as FirebaseStorage;
    functions = {} as Functions;
} else {
    try {
        db = getFirestore(app);
        console.log("Firestore initialized");
    } catch (error) {
        console.error("Firestore initialization error caught:", error);
        console.error("!!! Firestore failed to initialize. Exporting dummy db object. !!!");
        db = {} as Firestore;
    }
    
    try {
        auth = getAuth(app);
        console.log("Auth initialized");
    } catch (error) {
        console.error("Auth initialization error:", error);
        auth = {} as Auth;
    }
    
    try {
        storage = getStorage(app);
        console.log("Storage initialized");
    } catch (error) {
        console.error("Storage initialization error:", error);
        storage = {} as FirebaseStorage;
    }
    
    try {
        functions = getFunctions(app);
        console.log("Functions initialized");
    } catch (error) {
        console.error("Functions initialization error:", error);
        functions = {} as Functions;
    }
}

// Connect to Firebase emulators if running locally
if (import.meta.env.DEV) {
    try {
        // Always connect to all emulators in dev mode for consistency
        console.log("Connecting to Firestore emulator on localhost:8080");
        connectFirestoreEmulator(db, "localhost", 8080);
        console.log("Connecting to Auth emulator on localhost:9099");
        connectAuthEmulator(auth, "http://localhost:9099");
        console.log("Connecting to Functions emulator on localhost:5001");
        connectFunctionsEmulator(functions, "localhost", 5001);
    } catch (error) {
        console.error("Error connecting to emulators:", error);
    }
}

// FIXED: Simplified wrapper for httpsCallable with better error handling
export const safeHttpsCallable = async <T, R>(
  functionName: string, 
  data?: T
): Promise<{data: R}> => {
  console.log(`[Firebase] Calling Cloud Function: ${functionName} with data:`, data);
  
  try {
    // Check functions instance validity
    if (typeof functions !== 'object' || !functions || !('app' in functions)) {
      console.error(`[Firebase] Functions instance is invalid when trying to call ${functionName}`);
      throw new Error(`Functions instance is invalid. CORS errors might occur.`);
    }
    
    // In development mode, add the isEmulatorCall flag to bypass auth checks
    let callData = data;
    if (import.meta.env.DEV) {
      callData = {
        ...data,
        isEmulatorCall: true
      } as T;
      console.log(`[Firebase] Added isEmulatorCall flag for development mode`);
    }
    
    const callableFunction = httpsCallable<T, R>(functions, functionName);
    console.log(`[Firebase] Function callable created successfully for ${functionName}`);
    
    // Return a promise that will log the actual HTTP call
    return new Promise((resolve, reject) => {
      console.log(`[Firebase] Executing call to ${functionName}...`);
      callableFunction(callData)
        .then(result => {
          console.log(`[Firebase] Call to ${functionName} succeeded with result:`, result);
          resolve(result);
        })
        .catch(error => {
          console.error(`[Firebase] Call to ${functionName} failed:`, error);
          reject(error);
        });
    });
  } catch (error) {
    console.error(`[Firebase] Error setting up callable function ${functionName}:`, error);
    throw error;
  }
};

// ADDED: Log the exported functions object
console.log("[Firebase] Exporting functions object:", functions);

export { db, auth, storage, functions };
